package com.dotdrives.web.pages.forms

import com.dotdrives.model.FormPaymentReadiness
import com.dotdrives.web.ui.activateSurface
import com.dotdrives.controller.FormsBoardUiState
import com.dotdrives.model.GivingForm
import com.dotdrives.model.GivingFormStatus
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface FormsDashboardProps : Props {
    var state: FormsBoardUiState
    var canWrite: Boolean
    var orgId: String
    var onOpenForm: (GivingForm) -> Unit
    var onNewForm: () -> Unit
    var onConnectStripe: () -> Unit
    var onRetry: () -> Unit
}

val FormsDashboard = FC<FormsDashboardProps> { props ->
    val state = props.state

    if (state.loading && state.forms.isEmpty()) {
        FormsDashboardSkeleton()
        return@FC
    }

    if (state.error != null) {
        div {
            className = ClassName("forms-error-wrap")
            DotBanner {
                message = state.error!!
                onRetry = props.onRetry
                className = "forms-inline-banner"
            }
        }
        return@FC
    }

    if (state.stripeConnected == false && state.forms.isEmpty()) {
        PaymentsGate { onConnect = props.onConnectStripe }
        return@FC
    }

    if (state.forms.isEmpty()) {
        div {
            className = ClassName("forms-empty-wrap")
            DotEmptyState {
                emoji = "📝"
                title = "No giving forms yet"
                body = "Create a campaign, evergreen, or event form to start collecting gifts online."
                primaryAction = if (props.canWrite) DotAction("+ New form", props.onNewForm) else null
                secondaryAction = null
            }
        }
        return@FC
    }

    if (state.stripeConnected == false) {
        PaymentsGateBanner { onConnect = props.onConnectStripe }
    }

    if (state.paymentReadiness == FormPaymentReadiness.MIGRATION_PENDING || state.paymentReadiness == FormPaymentReadiness.RESTRICTED) {
        DotBanner { tone = com.dotdrives.design.DotTone.Info; message = state.paymentReadiness!!.message; className = "forms-inline-banner" }
    }

    val breakdown = activeFormsBreakdown(state.forms)

    div {
        className = ClassName("forms-stat-grid")
        FormsStatCard {
            label = "RAISED ONLINE · 30 DAYS"
            value = orgRaisedCell(state.orgStats)
            sub = orgGiftsSubtitle(state.orgStats)
        }
        FormsStatCard {
            label = "ACTIVE FORMS"
            value = breakdown.liveCount.toString()
            sub = breakdown.subtitle.ifEmpty { "No live forms yet" }
        }
        FormsStatCard {
            label = "RECURRING GIVERS"
            // Counts distinct givers whose application records are marked active. Imported
            // status is historical until provider reconciliation confirms each schedule.
            value = state.activePartnersCount?.toString() ?: "—"
            sub = if (state.paymentReadiness == FormPaymentReadiness.MIGRATION_PENDING)
                "Recorded as active · Includes unverified" else "Active recurring givers"
        }
    }

    div {
        className = ClassName("forms-card-grid")
        state.forms.forEach { form ->
            FormCard {
                key = form.id.unsafeCast<Key>()
                this.form = form
                stats = state.stats
                orgId = props.orgId
                onOpen = { props.onOpenForm(form) }
            }
        }
    }
}

private external interface FormsStatCardProps : Props {
    var label: String
    var value: String
    var sub: String
}

private val FormsStatCard = FC<FormsStatCardProps> { props ->
    div {
        className = ClassName("forms-stat-card")
        div { className = ClassName("forms-stat-label"); +props.label }
        div { className = ClassName("forms-stat-value"); +props.value }
        div { className = ClassName("forms-stat-sub"); +props.sub }
    }
}

private external interface FormCardProps : Props {
    var form: GivingForm
    var stats: Map<String, com.dotdrives.model.FormStats>?
    var orgId: String
    var onOpen: () -> Unit
}

private val FormCard = FC<FormCardProps> { props ->
    val form = props.form
    div {
        className = ClassName("forms-card dot-surface")
        onClick = { activateSurface(it) }
        div {
            className = ClassName("forms-card-head")
            div {
                div { className = ClassName("forms-card-name"); +form.name }
                div {
                    className = ClassName("forms-card-target")
                    +(form.targetName ?: "No target")
                }
            }
            DotPill {
                text = formStatusLabel(form.status)
                tone = formStatusTone(form.status)
            }
        }
        div {
            className = ClassName("forms-card-metrics")
            listOf(
                "RAISED" to formRaisedCell(props.stats, form.id),
                "GIFTS" to formGiftsCell(props.stats, form.id),
                "RECURRING" to formRecurringCell(props.stats, form.id),
            ).forEach { (label, value) ->
                div {
                    div { className = ClassName("forms-card-metric-label"); +label }
                    div { className = ClassName("forms-card-metric-value"); +value }
                }
            }
        }
        div {
            className = ClassName("forms-card-actions")
            button {
                type = ButtonType.buttonType
                className = ClassName("forms-card-action forms-card-action-primary dot-surface-action")
                onClick = { props.onOpen() }
                +"Edit form"
            }
            if (form.status == GivingFormStatus.LIVE) {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("forms-card-action forms-card-action-secondary")
                    onClick = { copyFormLink(props.orgId, form.publicSlug) }
                    +"🔗 Copy public link"
                }
            }
        }
    }
}

private val FormsDashboardSkeleton = FC<Props> {
    div {
        className = ClassName("forms-stat-grid")
        repeat(3) { index ->
            div {
                key = index.toString().unsafeCast<Key>()
                className = ClassName("forms-stat-card")
                DotSkeleton { height = "12px"; className = "dot-skeleton-mb" }
                DotSkeleton { height = "28px"; width = "60%" }
            }
        }
    }
    div {
        className = ClassName("forms-card-grid")
        repeat(4) { index ->
            div {
                key = index.toString().unsafeCast<Key>()
                className = ClassName("forms-card")
                DotSkeleton { height = "18px"; width = "70%" }
                DotSkeleton { height = "14px"; width = "40%" }
                DotSkeleton { height = "40px" }
            }
        }
    }
}
